declare global {
  interface Window {
    dataLayer: any[];
  }
}

interface PurchaseTrackingParams {
  transaction_id: string;
  value: number;
  currency: string;
  tier: 'weekly' | 'monthly' | 'annual';
  payment_method: 'apple_pay' | 'google_pay' | 'card';
  is_trial: boolean;
}

interface SignupTrackingParams {
  user_id: string;
  email: string;
  country: string;
  signup_method: 'email_otp';
  timestamp: string;
}

export function trackPurchaseConversion(params: PurchaseTrackingParams): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    console.warn('[Analytics] dataLayer not available');
    return;
  }

  // Clear previous ecommerce data
  window.dataLayer.push({ ecommerce: null });
  
  // Push purchase event
  window.dataLayer.push({
    event: 'purchase',
    ecommerce: {
      transaction_id: params.transaction_id,
      value: params.value,
      currency: params.currency,
      items: [{
        item_id: `subscription_${params.tier}`,
        item_name: `BUXBOX ${params.tier.charAt(0).toUpperCase() + params.tier.slice(1)} Subscription`,
        item_category: 'Subscription',
        price: params.value,
        quantity: 1
      }]
    },
    payment_method: params.payment_method,
    subscription_tier: params.tier,
    is_free_trial: params.is_trial
  });
  
  console.log('[Analytics] Purchase tracked:', params);
}

export function trackSignupConversion(params: SignupTrackingParams): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    console.warn('[Analytics] dataLayer not available');
    return;
  }

  // Push signup event to GTM
  window.dataLayer.push({
    event: 'sign_up',
    user_id: params.user_id,
    method: params.signup_method,
    country: params.country,
    signup_timestamp: params.timestamp
  });
  
  console.log('[Analytics] Signup tracked:', params);
}

// E-commerce tracking functions
export function trackBeginCheckout(params: {
  value: number;
  currency: string;
  items: Array<{
    item_id: string;
    item_name: string;
    item_category?: string;
    price: number;
    quantity?: number;
  }>;
}): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    console.warn('[Analytics] dataLayer not available');
    return;
  }

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'begin_checkout',
    ecommerce: {
      currency: params.currency,
      value: params.value,
      items: params.items
    }
  });
  
  console.log('[Analytics] Begin checkout tracked:', params);
}

export function trackAddPaymentInfo(params: {
  value: number;
  currency: string;
  payment_type: string;
  items: Array<{
    item_id: string;
    item_name: string;
    price: number;
  }>;
}): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    console.warn('[Analytics] dataLayer not available');
    return;
  }

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'add_payment_info',
    ecommerce: {
      currency: params.currency,
      value: params.value,
      payment_type: params.payment_type,
      items: params.items
    }
  });
  
  console.log('[Analytics] Add payment info tracked:', params);
}

export function trackViewItem(params: {
  value: number;
  currency: string;
  items: Array<{
    item_id: string;
    item_name: string;
    item_category?: string;
    price: number;
  }>;
}): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    console.warn('[Analytics] dataLayer not available');
    return;
  }

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'view_item',
    ecommerce: {
      currency: params.currency,
      value: params.value,
      items: params.items
    }
  });
  
  console.log('[Analytics] View item tracked:', params);
}

export function trackAddToCart(params: {
  value: number;
  currency: string;
  items: Array<{
    item_id: string;
    item_name: string;
    price: number;
    quantity: number;
  }>;
}): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    console.warn('[Analytics] dataLayer not available');
    return;
  }

  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({
    event: 'add_to_cart',
    ecommerce: {
      currency: params.currency,
      value: params.value,
      items: params.items
    }
  });
  
  console.log('[Analytics] Add to cart tracked:', params);
}

// Non-ecommerce tracking functions
export function trackFormSubmission(params: {
  form_id: string;
  form_name: string;
  form_destination?: string;
}): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    console.warn('[Analytics] dataLayer not available');
    return;
  }

  window.dataLayer.push({
    event: 'form_submission',
    form_id: params.form_id,
    form_name: params.form_name,
    form_destination: params.form_destination
  });
  
  console.log('[Analytics] Form submission tracked:', params);
}

export function trackButtonClick(params: {
  button_id?: string;
  button_text: string;
  button_url?: string;
  button_location?: string;
}): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    console.warn('[Analytics] dataLayer not available');
    return;
  }

  window.dataLayer.push({
    event: 'button_click',
    button_id: params.button_id,
    button_text: params.button_text,
    button_url: params.button_url,
    button_location: params.button_location
  });
  
  console.log('[Analytics] Button click tracked:', params);
}

export function trackSearch(params: {
  search_term: string;
  search_results_count?: number;
}): void {
  if (typeof window === 'undefined' || !window.dataLayer) {
    console.warn('[Analytics] dataLayer not available');
    return;
  }

  window.dataLayer.push({
    event: 'search',
    search_term: params.search_term,
    search_results_count: params.search_results_count
  });
  
  console.log('[Analytics] Search tracked:', params);
}
